<template>
  <PortalLayout section="Invite Codes">
    <div class="mt-4">
      <Loading v-if="isPropertyLoading || isLoadingCodes" />
      <div v-else-if="property" class>
        <div v-if="inviteCodes && inviteCodes.length > 0">
          <AddButton @click="addClick">Add Invite Code</AddButton>
          <div
            v-for="inviteCode in inviteCodes"
            :key="inviteCode.inviteCodeId"
            class="px-4 py-2 pt-3 my-2 bg-gray-100 border-t cursor-pointer hover:bg-teal-200"
            @click="editClick(inviteCode)"
          >
            <div>{{ inviteCode.code }}</div>
            <div class="ml-2 my-2 font-medium text-gray-700">
              <span v-if="inviteCode.enableStaffRoles"> Staff </span>
              <span
                v-for="tpId in inviteCode.trainingProgramIds"
                class="rounded border-gray-300 border-4 ml-2 py-1 px-1"
              >
                {{ lookupTrainingName(tpId) }}
              </span>
              <span v-if="inviteCode.activatedTenantId" class="ml-2">
                Activates Tenant:
                <strong>{{ lookupTenantName(inviteCode.activatedTenantId) }}</strong>
              </span>
              <span
                v-if="inviteCode.permissionTypes && inviteCode.permissionTypes.length > 0"
                class="ml-2"
              >
                Activates Permissions:
                <strong v-for="(permission, i) in inviteCode.permissionTypes">{{
                  getPermissionTypeString(permission) +
                  (i <= inviteCode.permissionTypes.length ? ", " : "")
                }}</strong>
              </span>
            </div>
          </div>
        </div>
        <div v-else class="mb-2">
          <span>-- No Invite codes found for this building --</span>
        </div>
        <div>
          <AddButton @click="addClick">Add Invite Code</AddButton>
        </div>

        <EditPopup
          entity-name="Invite Code"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="popupError"
          :delete-enabled="true"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <BaseInput
            type="text"
            v-model="codeValue"
            label="Invite Code"
            placeholder="(enter a unique invite code)"
            class="mb-2"
            :error="errors.codeValue"
          />
          <div class="h-4" />
          <BaseCheckbox label="Auto-Activate for SSO logins" v-model="autoActivateSSO" />
          <div class="h-4" />
          <BaseCheckbox label="Auto-Activate for v4 upgrades" v-model="autoActivateV4Upgrade" />
          <div class="h-4" />
          <BaseCheckbox
            label="Auto-Activate for verified emails"
            v-model="autoActivateForVerifiedEmails"
          />
          <div class="h-4" />
          <BaseCheckbox label="Enable Staff Roles" v-model="staffRolesEnabled" />
          <div class="h-4" />
          <BaseCheckboxGroup
            v-model="selectedTrainingPrograms"
            name="trainingProgram"
            label="Training Program"
            :options="trainingPrograms"
            vertical
          />
          <div class="h-4" />
          <BaseSelect
            class=""
            v-model="activatedTenantId"
            label="Activates Tenant"
            :options="tenantSelectboxOptions"
            :default-option="({ name: '--None--', value: -1 } as NameValue)"
          />
          <div class="h-4" />
          <BaseCheckboxGroup
            :model-value="selectedPermissionOptions"
            :options="permissionOptions"
            label="Additional Permissions"
            class="md:flex flex-wrap"
            checkbox-class="basis-1/2 xl:basis-1/3"
          />
        </EditPopup>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useTrainingStore } from "../../store/trainingStore";
import { InviteCodeDTO, PermissionType } from "../../store/generatedFetchClient";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { getPermissionTypeString } from "../../helpers";
import { NameValue } from "../../sharedTypes";

let permissionOptions = Object.values(PermissionType)
  .filter((p) => Number(p) >= 0)
  .map((p) => {
    return { name: getPermissionTypeString(p as PermissionType), value: p };
  });

const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isLoadingCodes = ref(false);
var property = computed(() => bldgStore.property);

const tenantSelectboxOptions = computed(
  () =>
    property.value.tenants.map((t) => ({
      name: t.name,
      value: t.tenantId,
    })) as NameValue[]
);

var trainingPrograms = computed(() => {
  let tp = trainingStore.portalTrainingPrograms;
  if (tp !== null && tp !== undefined) {
    return tp.map((t) => ({
      name: t.name,
      value: t.trainingProgramId,
    }));
  }
  return [];
});

function lookupTrainingName(trainingProgramId: number): string {
  let program = trainingPrograms?.value?.find((p) => p.value === trainingProgramId);
  return program?.name ?? "";
}

function lookupTenantName(tenantId: number | null): string {
  if (tenantId && property.value) {
    let tenants = property.value.tenants;
    if (tenants) {
      let tenant = tenants.find((t) => t.tenantId == tenantId);
      if (tenant && tenant.name) {
        return tenant.name;
      }
    }
  }
  return "";
}

var inviteCodes = computed(() => bldgStore.inviteCodes);

onMounted(loadData);
watch(property, loadData);

async function loadData() {
  isLoadingCodes.value = true;
  if (property.value?.portalId) {
    await bldgStore.getInviteCodes(property.value.portalId);
    await trainingStore.getTrainingProgramsByPortal(property.value.portalId);
  }
  isLoadingCodes.value = false;
}

// Popup stuff
let isEditing = ref(false);
let isPopupVisible = ref(false);
let popupError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    codeValue: yup.string().required().max(50).label("Invite Code"),
  }),
  initialValues: {
    codeValue: "",
  },
});

let inviteCodeId = ref(-1);
const { value: codeValue } = useField<string>("codeValue");
let staffRolesEnabled = ref(false);
let autoActivateSSO = ref(false);
let autoActivateV4Upgrade = ref(false);
let autoActivateForVerifiedEmails = ref(false);

let selectedTrainingPrograms = ref([] as Number[]);
let activatedTenantId = ref<number | null>(null);
let selectedPermissionOptions = ref<number[]>();

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveCode = {
    inviteCodeId: inviteCodeId.value,
    code: codeValue.value,
    portalId: property.value?.portalId,
    enableStaffRoles: staffRolesEnabled.value,
    autoActivateSSO: autoActivateSSO.value,
    autoActivateV4Upgrade: autoActivateV4Upgrade.value,
    autoActivateForVerifiedEmails: autoActivateForVerifiedEmails.value,
    trainingProgramIds: selectedTrainingPrograms.value,
    activatedTenantId: activatedTenantId.value > 0 ? activatedTenantId.value : null,
    dateCreated: new Date(),
    permissionTypes: selectedPermissionOptions.value.map((p) => p as PermissionType),
  } as InviteCodeDTO;

  console.log("Saving invite code", saveCode);
  popupError.value = "";

  if (property.value && property.value.portalId) {
    let possibleError = await bldgStore.saveInviteCode(property.value?.portalId, saveCode);
    if (!possibleError) {
      isPopupVisible.value = false;
    } else {
      //handle error
      popupError.value = possibleError;
    }
  }
}

function onInvalidSubmit(ctx: any) {
  popupError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let addClick = function () {
  inviteCodeId.value = -1;
  codeValue.value = "";
  staffRolesEnabled.value = false;
  autoActivateSSO.value = false;
  autoActivateV4Upgrade.value = false;
  autoActivateForVerifiedEmails.value = false;
  selectedTrainingPrograms.value = [];
  selectedPermissionOptions.value = [];
  popupError.value = "";
  activatedTenantId.value = null;

  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedCode: InviteCodeDTO) {
  inviteCodeId.value = selectedCode.inviteCodeId ?? -1;
  codeValue.value = selectedCode.code ?? "";
  staffRolesEnabled.value = selectedCode.enableStaffRoles ?? false;
  autoActivateSSO.value = selectedCode.autoActivateSSO ?? false;
  autoActivateV4Upgrade.value = selectedCode.autoActivateV4Upgrade ?? false;
  autoActivateForVerifiedEmails.value = selectedCode.autoActivateForVerifiedEmails ?? false;
  selectedTrainingPrograms.value = selectedCode.trainingProgramIds ?? [];
  activatedTenantId.value = selectedCode.activatedTenantId;
  selectedPermissionOptions.value = selectedCode.permissionTypes
    ? [...selectedCode.permissionTypes]
    : [];
  popupError.value = "";

  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && inviteCodeId.value > 0) {
    popupError.value = await bldgStore.deleteInviteCode(inviteCodeId.value);
    if (!popupError.value) {
      isPopupVisible.value = false;
    }
  }
}
</script>
